import { contextStore } from "../auth/context-store.js";
import { sessionRequestContext } from "../auth/request-context.js";
import { PermissionCode } from "../../permissions/permissions.js";
import {
  AI_CATEGORY_LABELS,
  AI_CATEGORY_ORDER,
  AI_FINDING_STATUS_LABELS,
  AI_REVIEW_STATUS_LABELS,
  AI_SEVERITY_LABELS,
  type AIAppliedChange,
  type AIReviewFinding,
  type AIReviewSubjectType
} from "../../domain/ai-review/ai-review-model.js";
import type { AIReviewState } from "../../application/services/ai-review-orchestrator.js";

const esc = (v: unknown): string => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const errorText = (e: unknown, fallback: string): string => (e as { safeMessage?: string })?.safeMessage || (e as Error)?.message || fallback;
const isOpen = (f: AIReviewFinding) => f.status === "OPEN" || f.status === "ACKNOWLEDGED";

/**
 * <ai-review-panel data-type="ME" data-id="…" [data-mode="consult"]>
 *
 * INSPEÇÃO AUTOMÁTICA IA de um documento — o MESMO componente em todos os
 * relatórios. Mostra o resumo por área (ortografia, gramática, terminologia,
 * coerência, resultados × conclusão, normas, unidades, validação), os
 * achados por campo com ACEITAR / REJEITAR / EDITAR MANUALMENTE /
 * JUSTIFICAR, e o histórico de alterações com DESFAZER.
 *
 * Não existe "corrigir tudo": só APLICAR CORREÇÕES SEGURAS (linguísticas).
 * Depois de qualquer alteração, emite `ai-review-applied` para a página
 * recarregar o relatório. Toda ação é conferida no serviço.
 */
export class AiReviewPanel extends HTMLElement {
  private state: AIReviewState | null = null;
  private error: string | null = null;
  private busy = false;
  private showFindings = false;
  private onlyPending = false;
  private changes: AIAppliedChange[] | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private editing: string | null = null;
  private justifying: string | null = null;

  connectedCallback(): void {
    this.addEventListener("click", e => this.onClick(e));
    void this.load();
  }

  disconnectedCallback(): void {
    if (this.pollTimer) clearTimeout(this.pollTimer);
  }

  private get type(): AIReviewSubjectType { return (this.dataset["type"] || "ME") as AIReviewSubjectType; }
  private get reportId(): string { return this.dataset["id"] || ""; }
  private get consult(): boolean { return this.dataset["mode"] === "consult"; }
  private get svc(): any { return (window as any).app.platform.inspectIaReviewService; }
  private can(p: string): boolean { return contextStore.hasPermission(p); }

  async load(): Promise<void> {
    if (!this.reportId) return;
    const wasRunning = this.state?.review?.status === "AI_REVIEW_RUNNING";
    try {
      this.state = await this.svc.getReviewState(sessionRequestContext(), this.type, this.reportId);
      // Revisão terminou aplicando correções seguras: o relatório mudou — a página relê.
      if (wasRunning && this.state?.review && this.state.review.status !== "AI_REVIEW_RUNNING" && this.state.review.autoAppliedCount > 0) {
        this.dispatchEvent(new CustomEvent("ai-review-applied", { bubbles: true, detail: { type: this.type, id: this.reportId } }));
      }
      this.error = null;
    } catch (e) {
      this.error = errorText(e, "Não foi possível carregar a revisão IA.");
    }
    this.render();
    if (this.state?.review?.status === "AI_REVIEW_RUNNING") {
      this.pollTimer = setTimeout(() => void this.load(), 800);
    }
  }

  private async act(run: () => Promise<unknown>, changesDocument: boolean): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.render();
    try {
      await run();
      this.error = null;
      if (changesDocument) this.dispatchEvent(new CustomEvent("ai-review-applied", { bubbles: true, detail: { type: this.type, id: this.reportId } }));
    } catch (e) {
      this.error = errorText(e, "A operação não pôde ser concluída.");
    }
    this.busy = false;
    this.editing = null;
    this.justifying = null;
    await this.load();
  }

  private onClick(e: Event): void {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-air]");
    if (!el) return;
    const action = el.dataset["air"];
    const fid = el.dataset["fid"] || "";
    const ctx = sessionRequestContext();
    switch (action) {
      case "run": void this.act(() => this.svc.requestReview(ctx, this.type, this.reportId), false); break;
      case "safe": void this.act(() => this.svc.applySafeCorrections(ctx, this.type, this.reportId), true); break;
      case "toggle": this.showFindings = !this.showFindings; this.onlyPending = false; this.render(); break;
      case "pending": this.showFindings = true; this.onlyPending = true; this.render(); break;
      case "accept": void this.act(() => this.svc.acceptFinding(ctx, fid), true); break;
      case "reject": {
        const reason = window.prompt("Motivo (opcional) para rejeitar esta sugestão:") ?? null;
        if (reason === null) return;
        void this.act(() => this.svc.rejectFinding(ctx, fid, reason), false);
        break;
      }
      case "ack": void this.act(() => this.svc.acknowledgeFinding(ctx, fid), false); break;
      case "edit": this.editing = fid; this.justifying = null; this.render(); break;
      case "justify": this.justifying = fid; this.editing = null; this.render(); break;
      case "cancel": this.editing = null; this.justifying = null; this.render(); break;
      case "save-edit": {
        const text = (this.querySelector(`#air-edit-${CSS.escape(fid)}`) as HTMLTextAreaElement | null)?.value ?? "";
        void this.act(() => this.svc.applyManualEdit(ctx, fid, text), true);
        break;
      }
      case "save-justify": {
        const text = (this.querySelector(`#air-just-${CSS.escape(fid)}`) as HTMLTextAreaElement | null)?.value ?? "";
        void this.act(() => this.svc.justifyFinding(ctx, fid, text), false);
        break;
      }
      case "changes": void this.loadChanges(); break;
      case "revert": {
        if (!window.confirm("Desfazer esta alteração? O texto volta a ser o anterior.")) return;
        const cid = el.dataset["cid"] || "";
        void this.act(async () => { await this.svc.revertChange(ctx, this.type, this.reportId, cid); this.changes = null; }, true);
        break;
      }
      case "retry": void this.load(); break;
    }
  }

  private async loadChanges(): Promise<void> {
    try {
      this.changes = this.changes ? null : await this.svc.listAppliedChanges(sessionRequestContext(), this.type, this.reportId);
    } catch (e) {
      this.error = errorText(e, "Não foi possível ler o histórico de alterações.");
    }
    this.render();
  }

  render(): void {
    const s = this.state;
    const review = s?.review ?? null;
    const findings = s?.findings ?? [];
    const running = review?.status === "AI_REVIEW_RUNNING";
    const canRun = !this.consult && this.can(PermissionCode.AiReviewExecute);
    const openSafe = findings.filter(f => f.status === "OPEN" && f.autoApplicable).length;
    const canApply = !this.consult && s?.editable && this.can(PermissionCode.AiReviewApplyLanguage) && !s?.stale;
    const statusKey = review ? review.status : "AI_REVIEW_NOT_STARTED";
    const tone = statusKey === "AI_REVIEW_PASSED" ? "ok" : statusKey === "AI_REVIEW_WARNING" ? "warn" : statusKey === "AI_REVIEW_NOT_STARTED" || statusKey === "AI_REVIEW_RUNNING" ? "neutral" : "bad";

    this.innerHTML = `
      <style>${STYLES}</style>
      <section class="air">
        <div class="air-head">
          <div>
            <div class="air-title">INSPEÇÃO AUTOMÁTICA IA — INSPECT IA REVIEW</div>
            <div class="air-sub">A IA identifica e auxilia; o profissional decide. Nada é aprovado, nenhum número é alterado.</div>
          </div>
          <span class="air-badge air-${tone}">${esc(AI_REVIEW_STATUS_LABELS[statusKey])}</span>
        </div>
        ${this.error ? `<div class="air-error">${esc(this.error)} <button type="button" class="air-link" data-air="retry">tentar novamente</button></div>` : ""}
        ${!s ? `<p class="air-muted">${this.error ? "" : "Carregando…"}</p>` : `
          ${s.stale ? `<div class="air-warnbox">O relatório foi alterado depois da última revisão: a revisão está DESATUALIZADA. Execute nova revisão antes de enviar para análise.</div>` : ""}
          ${review?.status === "AI_REVIEW_FAILED" ? `<div class="air-error">${esc(review.failureReason || "Serviço INSPECT IA REVIEW temporariamente indisponível.")}</div>` : ""}
          ${review && !running ? this.summaryHtml(findings, review.autoAppliedCount) : ""}
          ${running ? `<p class="air-muted">Revisão em execução…</p>` : ""}
          ${review ? `<div class="air-meta">Revisão de ${esc(new Date(review.startedAt).toLocaleString("pt-BR"))}${review.requestedByName ? ` por ${esc(review.requestedByName)}` : ""} · ${esc(review.modelProvider)} / ${esc(review.modelName)} ${esc(review.modelVersion)} · prompts ${esc(review.promptVersion)}</div>` : `<p class="air-muted">Nenhuma revisão IA executada para este documento.</p>`}
          <div class="air-gate air-gate-ok">
            <strong>STATUS DA REVISÃO IA:</strong> ${esc('note' in s.gate ? s.gate.note : (s.gate as any).reason || 'Revisão IA consultiva.')}
            <small style="display:block; opacity:0.85; margin-top:2px;">Resultado consultivo. O profissional responsável decide sobre o prosseguimento.</small>
          </div>
          <div class="air-actions">
            ${canRun ? `<button type="button" class="air-btn air-primary" data-air="run" ${this.busy || running ? "disabled" : ""}>${review ? "EXECUTAR NOVA REVISÃO" : "EXECUTAR REVISÃO IA"}</button>` : ""}
            ${canApply && openSafe > 0 ? `<button type="button" class="air-btn" data-air="safe" ${this.busy ? "disabled" : ""} title="Só correções de português classificadas como seguras. Números, TAGs, normas e o sentido técnico são preservados.">APLICAR CORREÇÕES SEGURAS (${openSafe})</button>` : ""}
            ${findings.length ? `<button type="button" class="air-btn" data-air="toggle">${this.showFindings && !this.onlyPending ? "OCULTAR ALERTAS" : "VER ALERTAS"}</button>` : ""}
            ${findings.some(isOpen) ? `<button type="button" class="air-btn" data-air="pending">REVISAR PENDÊNCIAS (${findings.filter(isOpen).length})</button>` : ""}
            ${this.can(PermissionCode.AiReviewHistory) ? `<button type="button" class="air-btn" data-air="changes">${this.changes ? "OCULTAR ALTERAÇÕES" : "ALTERAÇÕES APLICADAS"}</button>` : ""}
          </div>
          ${this.showFindings ? this.findingsHtml(this.onlyPending ? findings.filter(isOpen) : findings, !!s.editable && !this.consult && !s.stale) : ""}
          ${this.changes ? this.changesHtml(this.changes, !!s.editable && !this.consult) : ""}
        `}
      </section>`;
  }

  private summaryHtml(findings: readonly AIReviewFinding[], autoApplied: number): string {
    const rows = AI_CATEGORY_ORDER.map(cat => {
      const list = findings.filter(f => f.category === cat);
      const open = list.filter(isOpen);
      const applied = list.filter(f => f.status === "APPLIED" || f.status === "EDITED").length;
      let mark: string;
      if (open.some(f => f.severity === "CRITICO")) mark = `<span class="air-bad">🔴 ${open.length} inconsistência(s)</span>`;
      else if (open.some(f => f.severity === "TECNICO")) mark = `<span class="air-bad">⚠ ${open.length} alerta(s) técnico(s)</span>`;
      else if (open.length) mark = `<span class="air-warn">⚠ ${open.length} ${open.every(f => f.autoApplicable) ? "correção(ões) segura(s) pendente(s)" : "alerta(s)"}</span>`;
      else if (applied) mark = `<span class="air-ok">✓ ${applied} correção(ões) aplicada(s)</span>`;
      else mark = `<span class="air-ok">✓ OK</span>`;
      return `<div class="air-row"><span>${esc(AI_CATEGORY_LABELS[cat])}</span>${mark}</div>`;
    }).join("");
    const auto = autoApplied > 0 ? `<div class="air-auto air-ok">✓ ${autoApplied} correção(ões) segura(s) de português aplicada(s) automaticamente antes desta revisão — veja em ALTERAÇÕES APLICADAS.</div>` : "";
    return `${auto}<div class="air-summary">${rows}</div>`;
  }

  private findingsHtml(list: readonly AIReviewFinding[], editable: boolean): string {
    if (list.length === 0) return `<p class="air-muted">Nenhum item.</p>`;
    const canResolve = this.can(PermissionCode.AiReviewResolveFinding) && !this.consult;
    return `<div class="air-findings">${list.map(f => {
      const actions: string[] = [];
      if (canResolve && isOpen(f)) {
        const technical = f.severity === "TECNICO" || f.severity === "CRITICO";
        if (!technical && f.suggestedText && editable) actions.push(`<button type="button" class="air-btn sm air-primary" data-air="accept" data-fid="${esc(f.id)}">ACEITAR</button>`);
        if (!technical) actions.push(`<button type="button" class="air-btn sm" data-air="reject" data-fid="${esc(f.id)}">REJEITAR</button>`);
        if (technical && f.status === "OPEN" && f.category !== "VALIDACAO") actions.push(`<button type="button" class="air-btn sm" data-air="ack" data-fid="${esc(f.id)}" title="Reconhecer que o alerta procede — o relatório ainda precisa ser corrigido.">ACEITAR ALERTA</button>`);
        if (f.justifiable) actions.push(`<button type="button" class="air-btn sm" data-air="justify" data-fid="${esc(f.id)}">JUSTIFICAR</button>`);
        if (editable && f.fieldPath && f.category !== "VALIDACAO") actions.push(`<button type="button" class="air-btn sm" data-air="edit" data-fid="${esc(f.id)}">EDITAR MANUALMENTE</button>`);
      }
      const risk = f.severity === "CRITICO" ? "ALTO" : f.severity === "TECNICO" ? "MÉDIO" : f.severity === "TERMINOLOGICO" ? "MÉDIO — possível mudança de sentido" : "BAIXO";
      return `
        <article class="air-f air-sev-${f.severity}">
          <div class="air-f-head">
            <span class="air-sev">${esc(AI_SEVERITY_LABELS[f.severity])}</span>
            <span class="air-cat">${esc(AI_CATEGORY_LABELS[f.category])}</span>
            <span class="air-field">${esc(f.fieldLabel)}</span>
            <span class="air-st">${esc(AI_FINDING_STATUS_LABELS[f.status])}</span>
          </div>
          <p class="air-exp"><strong>Motivo:</strong> ${esc(f.explanation)} <span class="air-muted">· Risco: ${esc(risk)}${f.autoApplicable ? " · correção segura" : ""}</span></p>
          ${f.originalText && f.category !== "RESULTADOS_CONCLUSAO" && f.category !== "COERENCIA" ? `<div class="air-diff"><div><span>TEXTO ORIGINAL</span><del>${esc(f.originalText)}</del></div>${f.suggestedText ? `<div><span>SUGESTÃO</span><ins>${esc(f.suggestedText)}</ins></div>` : ""}</div>` : ""}
          ${f.resolutionNote ? `<p class="air-muted">${esc(f.resolutionNote)}</p>` : ""}
          ${this.editing === f.id ? `<div class="air-form"><label>${f.originalText && f.category !== "RESULTADOS_CONCLUSAO" && f.category !== "COERENCIA" ? "Novo texto para o trecho" : `Novo texto do campo "${esc(f.fieldLabel)}"`}<textarea id="air-edit-${esc(f.id)}" rows="3">${esc(f.suggestedText || f.originalText)}</textarea></label><div class="air-actions"><button type="button" class="air-btn sm air-primary" data-air="save-edit" data-fid="${esc(f.id)}">APLICAR</button><button type="button" class="air-btn sm" data-air="cancel">CANCELAR</button></div></div>` : ""}
          ${this.justifying === f.id ? `<div class="air-form"><label>Justificativa técnica (fica registrada e visível ao PLH)<textarea id="air-just-${esc(f.id)}" rows="3"></textarea></label><div class="air-actions"><button type="button" class="air-btn sm air-primary" data-air="save-justify" data-fid="${esc(f.id)}">REGISTRAR JUSTIFICATIVA</button><button type="button" class="air-btn sm" data-air="cancel">CANCELAR</button></div></div>` : ""}
          ${actions.length && this.editing !== f.id && this.justifying !== f.id ? `<div class="air-actions">${actions.join("")}</div>` : ""}
          ${f.category === "VALIDACAO" && isOpen(f) ? `<p class="air-muted">Corrija no próprio formulário do relatório; a próxima revisão confere de novo.</p>` : ""}
        </article>`;
    }).join("")}</div>`;
  }

  private changesHtml(list: readonly AIAppliedChange[], editable: boolean): string {
    if (list.length === 0) return `<p class="air-muted">Nenhuma alteração aplicada pela revisão IA.</p>`;
    const KIND: Record<string, string> = { AUTO_SAFE: "Correção segura automática", ACCEPTED: "Sugestão aceita", MANUAL_EDIT: "Edição manual", ASSISTANT: "Assistente (aplicado ao campo)", REVERT: "Desfeita" };
    return `<div class="air-findings">${[...list].reverse().map(c => `
      <article class="air-f">
        <div class="air-f-head"><span class="air-cat">${esc(KIND[c.kind] ?? c.kind)}</span><span class="air-field">${esc(c.fieldPath)}</span><span class="air-st">${esc(new Date(c.at).toLocaleString("pt-BR"))}</span></div>
        <div class="air-diff"><div><span>ANTES</span><del>${esc(c.before)}</del></div><div><span>DEPOIS</span><ins>${esc(c.after)}</ins></div></div>
        ${editable && c.kind !== "REVERT" && this.can(PermissionCode.AiReviewResolveFinding) ? `<div class="air-actions"><button type="button" class="air-btn sm" data-air="revert" data-cid="${esc(c.id)}">DESFAZER</button></div>` : ""}
      </article>`).join("")}</div>`;
  }
}

const STYLES = `
  ai-review-panel { display:block; }
  .air { border:1px solid var(--ia-line, #3A3F45); border-radius:8px; padding:14px; background: var(--ia-bg-panel, #1F2328); color: var(--ia-fg, #E5E7EB); margin-top:14px; }
  .air-head { display:flex; justify-content:space-between; gap:12px; align-items:flex-start; flex-wrap:wrap; }
  .air-title { font-weight:700; font-size:0.8rem; letter-spacing:0.04em; }
  .air-sub, .air-muted, .air-meta { font-size:0.72rem; color: var(--ia-fg-muted, #9CA3AF); }
  .air-meta { margin-top:6px; }
  .air-badge { padding:3px 10px; border-radius:12px; font-size:0.68rem; font-weight:700; white-space:nowrap; }
  .air-ok { color: var(--ia-success-fg, #a7f3d0); } .air-warn { color: var(--ia-warning-fg, #fbbf24); } .air-bad { color: var(--ia-danger-fg, #fca5a5); } .air-neutral { color: var(--ia-fg-soft, #AEAEAE); }
  .air-badge.air-ok { background: var(--ia-success-bg, rgba(16,185,129,0.15)); } .air-badge.air-warn { background: var(--ia-warning-bg, rgba(245,158,11,0.15)); } .air-badge.air-bad { background: var(--ia-danger-bg, rgba(239,68,68,0.15)); } .air-badge.air-neutral { background: rgba(148,163,184,0.15); }
  .air-summary { display:grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap:4px 16px; margin:10px 0 4px; font-size:0.76rem; }
  .air-auto { font-size:0.74rem; margin-top:8px; }
  .air-row { display:flex; justify-content:space-between; gap:8px; border-bottom:1px dashed var(--ia-line, #3A3F45); padding:4px 0; }
  .air-row > span:first-child { font-weight:600; font-size:0.68rem; letter-spacing:0.04em; }
  .air-gate { margin-top:8px; font-size:0.74rem; padding:6px 10px; border-radius:6px; }
  .air-gate-ok { background: var(--ia-success-bg, rgba(16,185,129,0.12)); color: var(--ia-success-fg, #a7f3d0); }
  .air-gate-no { background: var(--ia-danger-bg, rgba(239,68,68,0.12)); color: var(--ia-danger-fg, #fca5a5); }
  .air-actions { display:flex; gap:6px; flex-wrap:wrap; margin-top:10px; }
  .air-btn { background: var(--ia-bg-raised, #2A2F35); color: var(--ia-fg, #E5E7EB); border:1px solid var(--ia-line, #3A3F45); padding:6px 10px; border-radius:6px; font-size:0.68rem; font-weight:700; cursor:pointer; }
  .air-btn.sm { padding:4px 8px; font-size:0.64rem; }
  .air-btn:disabled { opacity:0.5; cursor:not-allowed; }
  .air-primary { background: var(--ia-action-bg, #3C3C3C); color: var(--ia-action-fg, #fff); border-color: transparent; }
  .air-link { background:none; border:none; color:inherit; text-decoration:underline; cursor:pointer; font-size:0.72rem; }
  .air-error { margin-top:8px; border:1px solid rgba(239,68,68,0.45); background: rgba(239,68,68,0.10); color: var(--ia-danger-fg, #fca5a5); border-radius:6px; padding:8px 10px; font-size:0.74rem; }
  .air-warnbox { margin-top:8px; border:1px solid rgba(245,158,11,0.45); background: rgba(245,158,11,0.10); color: var(--ia-warning-fg, #fde68a); border-radius:6px; padding:8px 10px; font-size:0.74rem; }
  .air-findings { display:flex; flex-direction:column; gap:8px; margin-top:10px; }
  .air-f { border:1px solid var(--ia-line, #3A3F45); border-left-width:3px; border-radius:6px; padding:8px 10px; background: var(--ia-bg-inset, #15181B); }
  .air-sev-CRITICO { border-left-color: var(--ia-danger-fg, #ef4444); } .air-sev-TECNICO { border-left-color: var(--ia-warning-fg, #f59e0b); } .air-sev-TERMINOLOGICO { border-left-color: var(--ia-info-fg, #60a5fa); } .air-sev-LINGUISTICO { border-left-color: var(--ia-success-fg, #10b981); }
  .air-f-head { display:flex; gap:8px; flex-wrap:wrap; align-items:center; font-size:0.66rem; letter-spacing:0.03em; }
  .air-sev { font-weight:800; } .air-cat { color: var(--ia-fg-soft, #AEAEAE); } .air-field { font-weight:600; } .air-st { margin-left:auto; color: var(--ia-fg-muted, #9CA3AF); }
  .air-exp { font-size:0.76rem; margin:6px 0; line-height:1.45; }
  .air-diff { display:grid; grid-template-columns: 1fr 1fr; gap:8px; font-size:0.76rem; }
  .air-diff span { display:block; font-size:0.62rem; letter-spacing:0.04em; color: var(--ia-fg-muted, #9CA3AF); margin-bottom:2px; }
  .air-diff del { text-decoration: line-through; color: var(--ia-danger-fg, #fca5a5); } .air-diff ins { text-decoration:none; color: var(--ia-success-fg, #a7f3d0); }
  .air-form label { display:flex; flex-direction:column; gap:4px; font-size:0.68rem; margin-top:6px; }
  .air-form textarea { background: var(--ia-bg-panel, #1F2328); color: var(--ia-fg, #E5E7EB); border:1px solid var(--ia-line, #3A3F45); border-radius:5px; padding:6px 8px; font-family:inherit; font-size:0.8rem; }
  @media (max-width: 640px) { .air-diff { grid-template-columns: 1fr; } }
`;

if (!customElements.get("ai-review-panel")) customElements.define("ai-review-panel", AiReviewPanel);
